import type { TaskId } from '../domain/branded.js';
import type { Dependency } from '../domain/dependency.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';
import {
  CircularDependencyError,
  InvalidDependencyError,
  InvalidHierarchyError,
} from '../domain/errors.js';

/**
 * 排程用的圖結構建立與拓撲排序。
 *
 * 這裡只處理「順序」，不碰任何日期；所有日期運算都在 scheduling.ts 走日曆引擎。
 *
 * 圖上有兩種邊：
 * 1. 相依邊 predecessor -> successor（來自 `Project.dependencies`）。
 * 2. 階層邊 child -> parent（摘要任務的日期由子任務彙總而來，故子必須先算）。
 *
 * 另外，指向「摘要任務」的 FS/SS 相依會被下推（propagate）到其所有子孫，
 * 因為摘要的開始日 = 子任務最早開始日，限制其開始等同限制所有子孫的開始。
 */

/** 任務在階層與相依圖上的靜態關係。 */
export interface ScheduleGraph {
  /** 依 id 查任務（順序同 `Project.tasks`）。 */
  readonly tasksById: ReadonlyMap<string, Task>;
  /** 原始任務順序（決定所有輸出的穩定排序）。 */
  readonly order: readonly Task[];
  /** 每個任務的子任務（順序同 `Project.tasks`）。 */
  readonly childrenByParent: ReadonlyMap<string, readonly Task[]>;
  /** 實際參與計算的相依（含下推到子孫的摘要相依），以 successor 為 key。 */
  readonly incomingByTask: ReadonlyMap<string, readonly Dependency[]>;
  /** 同上，以 predecessor 為 key。 */
  readonly outgoingByTask: ReadonlyMap<string, readonly Dependency[]>;
  /** 拓撲順序（相依邊 + 階層邊）。 */
  readonly topoOrder: readonly Task[];
  /** 被下推到子孫的摘要相依原件，供診斷使用。 */
  readonly propagatedSummaryDependencies: readonly Dependency[];
}

const isSummaryLike = (task: Task, children: ReadonlyMap<string, readonly Task[]>): boolean =>
  (children.get(task.id as string)?.length ?? 0) > 0;

/**
 * 建立階層索引，並驗證 parentId 合法性。
 * @throws {InvalidHierarchyError}
 */
function buildHierarchy(tasks: readonly Task[], byId: ReadonlyMap<string, Task>): Map<string, Task[]> {
  const children = new Map<string, Task[]>();
  for (const task of tasks) {
    const parentId = task.parentId;
    if (parentId === null) continue;
    const parent = byId.get(parentId as string);
    if (parent === undefined) {
      throw new InvalidHierarchyError(task.id, `parent ${parentId} does not exist`);
    }
    if ((parent.id as string) === (task.id as string)) {
      throw new InvalidHierarchyError(task.id, 'task cannot be its own parent');
    }
    const bucket = children.get(parentId as string);
    if (bucket === undefined) children.set(parentId as string, [task]);
    else bucket.push(task);
  }

  // 偵測階層成環（a -> b -> a），否則後續走訪會無限迴圈。
  const state = new Map<string, number>(); // 0 = 進行中, 1 = 已確認無環
  for (const task of tasks) {
    const path: string[] = [];
    let current: Task | undefined = task;
    while (current !== undefined) {
      const key = current.id as string;
      const seen = state.get(key);
      if (seen === 1) break;
      if (seen === 0) {
        throw new InvalidHierarchyError(current.id, `circular outline: ${[...path, key].join(' -> ')}`);
      }
      state.set(key, 0);
      path.push(key);
      const nextParentId: TaskId | null = current.parentId;
      current = nextParentId === null ? undefined : byId.get(nextParentId as string);
    }
    for (const key of path) state.set(key, 1);
  }
  return children;
}

/** 收集某摘要任務的所有葉子子孫（前序、去重）。 */
function leafDescendants(
  root: Task,
  children: ReadonlyMap<string, readonly Task[]>,
): readonly Task[] {
  const leaves: Task[] = [];
  const stack: Task[] = [root];
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) continue;
    const kids = children.get(current.id as string);
    if (kids === undefined || kids.length === 0) {
      if ((current.id as string) !== (root.id as string)) leaves.push(current);
      continue;
    }
    for (let i = kids.length - 1; i >= 0; i -= 1) {
      const kid = kids[i];
      if (kid !== undefined) stack.push(kid);
    }
  }
  return leaves;
}

/**
 * 在有向圖上找一條環路，回傳 `[a, ..., a]`（首尾同一個 id）。
 * 以迭代式 DFS 實作，避免深鏈造成 stack overflow。
 */
function findCycle(
  nodes: readonly string[],
  adjacency: ReadonlyMap<string, readonly string[]>,
): readonly string[] | null {
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const colour = new Map<string, number>();
  for (const node of nodes) colour.set(node, WHITE);

  for (const root of nodes) {
    if (colour.get(root) !== WHITE) continue;
    const stack: { node: string; index: number }[] = [{ node: root, index: 0 }];
    colour.set(root, GREY);
    while (stack.length > 0) {
      const frame = stack[stack.length - 1];
      if (frame === undefined) break;
      const neighbours = adjacency.get(frame.node) ?? [];
      if (frame.index >= neighbours.length) {
        colour.set(frame.node, BLACK);
        stack.pop();
        continue;
      }
      const next = neighbours[frame.index];
      frame.index += 1;
      if (next === undefined) continue;
      const nextColour = colour.get(next);
      if (nextColour === GREY) {
        const cycle: string[] = [];
        let started = false;
        for (const entry of stack) {
          if (entry.node === next) started = true;
          if (started) cycle.push(entry.node);
        }
        cycle.push(next);
        return cycle;
      }
      if (nextColour === WHITE) {
        colour.set(next, GREY);
        stack.push({ node: next, index: 0 });
      }
    }
  }
  return null;
}

const toTaskIds = (ids: readonly string[]): readonly TaskId[] => ids as readonly TaskId[];

/**
 * 建立排程圖並做拓撲排序。
 *
 * @throws {InvalidDependencyError} 相依指向不存在的任務或自我相依。
 * @throws {InvalidHierarchyError} 大綱結構不合法。
 * @throws {CircularDependencyError} 相依（或相依 + 階層）成環。
 */
export function buildScheduleGraph(project: Project): ScheduleGraph {
  const tasks = project.tasks;
  const byId = new Map<string, Task>();
  for (const task of tasks) {
    const key = task.id as string;
    if (byId.has(key)) {
      throw new InvalidHierarchyError(task.id, 'duplicate task id');
    }
    byId.set(key, task);
  }

  const children = buildHierarchy(tasks, byId);

  // --- 1. 驗證相依並建立純相依鄰接表（用於產生「純相依環」的錯誤訊息）---
  const rawAdjacency = new Map<string, string[]>();
  for (const task of tasks) rawAdjacency.set(task.id as string, []);
  for (const dep of project.dependencies) {
    const from = dep.predecessorId as string;
    const to = dep.successorId as string;
    if (!byId.has(from)) {
      throw new InvalidDependencyError(dep.predecessorId, dep.successorId, `predecessor ${from} does not exist`);
    }
    if (!byId.has(to)) {
      throw new InvalidDependencyError(dep.predecessorId, dep.successorId, `successor ${to} does not exist`);
    }
    if (from === to) {
      throw new InvalidDependencyError(dep.predecessorId, dep.successorId, 'a task cannot depend on itself');
    }
    rawAdjacency.get(from)?.push(to);
  }

  const nodeIds = tasks.map((task) => task.id as string);
  const rawCycle = findCycle(nodeIds, rawAdjacency);
  if (rawCycle !== null) {
    throw new CircularDependencyError(toTaskIds(rawCycle));
  }

  // --- 2. 展開有效相依（摘要的 FS/SS 前置下推到子孫）---
  const incoming = new Map<string, Dependency[]>();
  const outgoing = new Map<string, Dependency[]>();
  const propagated: Dependency[] = [];
  const addDep = (dep: Dependency): void => {
    const to = dep.successorId as string;
    const from = dep.predecessorId as string;
    const inBucket = incoming.get(to);
    if (inBucket === undefined) incoming.set(to, [dep]);
    else inBucket.push(dep);
    const outBucket = outgoing.get(from);
    if (outBucket === undefined) outgoing.set(from, [dep]);
    else outBucket.push(dep);
  };

  for (const dep of project.dependencies) {
    const successor = byId.get(dep.successorId as string);
    if (successor === undefined) continue;
    const successorIsSummary = isSummaryLike(successor, children);
    if (successorIsSummary && (dep.type === 'FS' || dep.type === 'SS')) {
      // 摘要的開始 = 子任務最早開始，故限制下推到每個葉子子孫。
      for (const leaf of leafDescendants(successor, children)) {
        addDep({ ...dep, successorId: leaf.id });
      }
      propagated.push(dep);
      continue;
    }
    addDep(dep);
  }

  // --- 3. 組合圖（有效相依邊 + child -> parent 階層邊）並拓撲排序 ---
  const adjacency = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const id of nodeIds) {
    adjacency.set(id, []);
    indegree.set(id, 0);
  }
  const link = (from: string, to: string): void => {
    adjacency.get(from)?.push(to);
    indegree.set(to, (indegree.get(to) ?? 0) + 1);
  };
  for (const [, deps] of outgoing) {
    for (const dep of deps) link(dep.predecessorId as string, dep.successorId as string);
  }
  for (const task of tasks) {
    if (task.parentId !== null) link(task.id as string, task.parentId as string);
  }

  const queue: string[] = [];
  for (const id of nodeIds) {
    if ((indegree.get(id) ?? 0) === 0) queue.push(id);
  }
  const topoOrder: Task[] = [];
  let head = 0;
  while (head < queue.length) {
    const current = queue[head];
    head += 1;
    if (current === undefined) continue;
    const task = byId.get(current);
    if (task !== undefined) topoOrder.push(task);
    for (const next of adjacency.get(current) ?? []) {
      const remaining = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, remaining);
      if (remaining === 0) queue.push(next);
    }
  }

  if (topoOrder.length !== tasks.length) {
    const remaining = nodeIds.filter((id) => (indegree.get(id) ?? 0) > 0);
    const cycle = findCycle(remaining, adjacency) ?? remaining;
    throw new CircularDependencyError(toTaskIds(cycle));
  }

  return {
    tasksById: byId,
    order: tasks,
    childrenByParent: children,
    incomingByTask: incoming,
    outgoingByTask: outgoing,
    topoOrder,
    propagatedSummaryDependencies: propagated,
  };
}
